<template>
  <div
    class="category-card"
    role="button"
    tabindex="0"
    :aria-label="ariaLabel"
    @click="handleClick"
    @keydown.enter.prevent="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <md-icon class="category-icon" aria-hidden="true">{{ icon }}</md-icon>
    <div class="category-content">
      <h3 class="category-title">{{ title }}</h3>
      <p class="category-description">{{ description }}</p>
      <div class="category-task-count" v-if="taskCount !== null">
        {{ taskCount }} task{{ taskCount !== 1 ? 's' : '' }}
      </div>
    </div>
    <span class="category-go" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
        <path d="M5 12h14M13 6l6 6-6 6" />
      </svg>
    </span>
  </div>
</template>

<script>
export default {
  name: 'CategoryCard',
  props: {
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      required: true,
    },
    taskCount: {
      type: Number,
      default: null,
    },
    color: {
      type: String,
      default: 'primary',
      validator: value => ['primary', 'success', 'info', 'warning'].includes(value),
    },
    redirectTo: {
      type: String,
      default: null,
    },
  },
  computed: {
    ariaLabel() {
      const count =
        this.taskCount !== null
          ? `, ${this.taskCount} task${this.taskCount !== 1 ? 's' : ''}`
          : '';
      return `${this.title}. ${this.description}${count}`;
    },
  },
  methods: {
    handleClick() {
      if (this.redirectTo) {
        this.$router.push({ name: this.redirectTo });
      }
      this.$emit('click');
    },
  },
};
</script>

<style lang="scss" scoped>
// Style H: large filled icon in the brand color, left aligned text, round arrow bottom right.
// --card-brand is set by the dashboard from the school/org primary color.
.category-card {
  --brand: var(--card-brand, var(--primary-color, #3b86f5));
  background: #ffffff;
  border-radius: 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
  padding: 26px 24px 22px;
  text-align: left;
  box-shadow: 0 1px 2px rgba(20, 28, 40, 0.06), 0 8px 24px -12px rgba(20, 28, 40, 0.18);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover,
  &:focus-visible {
    transform: translateY(-2px);
    box-shadow: 0 2px 4px rgba(20, 28, 40, 0.08), 0 14px 30px -12px rgba(20, 28, 40, 0.28);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 3px solid #ffffff;
    outline-offset: 2px;
  }

  .category-icon {
    color: var(--brand) !important;
    font-size: 40px !important;
    width: 40px;
    height: 40px;
    line-height: 1;
  }

  .category-content {
    flex: 1;
    width: 100%;
    min-width: 0;
    padding-top: 14px;
  }

  .category-title {
    font-size: 19px;
    font-weight: 700;
    color: #181818;
    margin: 0 0 6px 0;
    line-height: 1.25;
  }

  .category-description {
    font-size: 14px;
    color: #58615c;
    margin: 0;
    line-height: 1.4;
  }

  .category-task-count {
    font-size: 13px;
    color: #58615c;
    font-weight: 500;
    margin-top: 6px;
  }

  // Round arrow button tinted with the school color; fills solid on hover.
  // The arrow is inline SVG so theme icon colors can't override it.
  .category-go {
    align-self: flex-end;
    flex: none;
    margin-top: 18px;
    width: 34px;
    height: 34px;
    color: var(--brand);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f1f1f1;
    background: color-mix(in srgb, var(--brand) 12%, #ffffff);
    transition: background 0.2s ease, color 0.2s ease;
  }

  &:hover .category-go,
  &:focus-visible .category-go {
    background: var(--brand);
    color: #ffffff;
  }
}

@media (prefers-reduced-motion: reduce) {
  .category-card,
  .category-card .category-go {
    transition: none;
  }
}

@media (max-width: 768px) {
  .category-card {
    padding: 20px 18px 18px;

    .category-icon {
      font-size: 34px !important;
      width: 34px;
      height: 34px;
    }

    .category-title {
      font-size: 17px;
    }

    .category-description {
      font-size: 13px;
    }

    .category-go {
      margin-top: 14px;
    }
  }
}
</style>
